import { styled } from '@mui/material';
import React from 'react';
import { Trans } from 'react-i18next';
import { CellRendererParams } from 'routes/BudgetModule/config/types';
import { Link, useLocation } from 'react-router-dom';
import Loading from 'components/Loading';
import { getFormattedDateString } from './helpers';
import { Notification, NotificationColumn } from './types';

const StyDate = styled('span', {
    name: 'StyDate',
})(() => ({
    fontWeight: 300,
    letterSpacing: '0.42855px',
    color: 'black',
}));

const StyContainer = styled('div', {
    name: 'StyContainer',
    shouldForwardProp: (prop) => prop !== 'isRead',
})<{ isRead: boolean }>(({ theme, isRead }) => ({
    backgroundColor: isRead ? 'white' : theme.palette.primary.background,
    paddingLeft: '0.75rem',
    lineHeight: '18px',
    height: '100%',
    whiteSpace: 'pre-line',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    alignItems: 'center',
}));

// this cellRenderer is used for both columns date and Notification, this is why we check de colId
export default function NotificationCellRenderer({
    data,
    colId,
}: CellRendererParams<Notification> & {
    colId: NotificationColumn;
}): React.ReactElement {
    const location = useLocation();

    if (data === undefined) {
        return (
            <StyContainer isRead>
                <Loading />
            </StyContainer>
        );
    }
    const content =
        colId === NotificationColumn.CONTENT ? (
            <Trans
                i18nKey={`notification.reasons.${data.key}`}
                tOptions={{
                    actorName: data.actorName,
                    redirectTo: data.redirectTo,
                    entityLabel: data.entityLabel,
                }}
                components={{
                    bold: (
                        <strong
                            style={{
                                color: 'black',
                            }}
                        />
                    ),
                }}
            />
        ) : (
            <StyDate>{getFormattedDateString(data.createdAt)}</StyDate>
        );

    const searchPart = data.redirectTo.search
        ? { search: encodeURI(data.redirectTo.search) }
        : {};

    const hashPart = data.redirectTo.hash
        ? { hash: encodeURI(data.redirectTo.hash) }
        : {};

    return (
        <Link
            to={{
                pathname: data.redirectTo.pathname
                    .split('/')
                    .map(encodeURIComponent)
                    .join('/'),
                ...searchPart,
                ...hashPart,
            }}
            state={{ parentLocation: location }}
            style={{ textDecoration: 'inherit', color: 'inherit' }}
        >
            <StyContainer isRead={data.read}>{content}</StyContainer>
        </Link>
    );
}
